/**
 * Time, as the desk stores it and as the mockup shows it.
 *
 * EVERY TIMESTAMP FROM THE DESK IS A SQLITE STRING IN UTC — 'YYYY-MM-DD
 * HH:MM:SS', no zone marker. Handing that to `new Date()` parses it as LOCAL
 * time in some browsers and UTC in others, which in Brisbane is a silent ten
 * hour error: a card raised nine minutes ago reads "10h 09m", and the operator
 * decides it is stale. So nothing in this app calls Date.parse on a desk
 * timestamp directly; it goes through parseUtc.
 */

/** A SQLite UTC timestamp, or an ISO one, as epoch milliseconds. */
export function parseUtc(stamp: string | null | undefined): number | null {
  if (!stamp) return null;
  const s = String(stamp).trim();
  // Add the zone marker unless the string already carries one.
  const iso = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s) ? s.replace(' ', 'T') : `${s.replace(' ', 'T')}Z`;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/**
 * How long ago, in the mockup's own shapes: "9m", "52m", "1h 05m", "3h 12m",
 * "2d 4h". Minutes are zero-padded inside an hours string so a column of ages
 * stays a column.
 */
export function ageOf(stamp: string | null | undefined, now: number = Date.now()): string {
  const t = parseUtc(stamp);
  if (t === null) return '—';
  return duration(Math.max(0, now - t));
}

/** Time remaining until a deadline. Past deadlines read "0m", never negative. */
export function until(stamp: string | null | undefined, now: number = Date.now()): string {
  const t = parseUtc(stamp);
  if (t === null) return '—';
  return duration(Math.max(0, t - now));
}

/** Has this deadline passed? */
export function isPast(stamp: string | null | undefined, now: number = Date.now()): boolean {
  const t = parseUtc(stamp);
  return t === null ? false : t <= now;
}

function duration(ms: number): string {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${String(mins % 60).padStart(2, '0')}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

/** "Tue 09:41", in the operator's own timezone — he reads it, not a server. */
export function whenLocal(stamp: string | null | undefined): string {
  const t = parseUtc(stamp);
  if (t === null) return '—';
  const d = new Date(t);
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}

/** "3 Oct 13:02" in Brisbane (UTC+10, no DST): the trial panel's lines, whatever zone the browser is in (fix-83). */
export function whenBrisbane(stamp: string | null | undefined): string {
  const t = parseUtc(stamp);
  if (t === null) return '—';
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(t));
  const v = (k: string) => parts.find((x) => x.type === k)?.value ?? '';
  return `${v('day')} ${v('month')} ${v('hour')}:${v('minute')}`;
}

/** "14 Sep" — a date with no year, for things inside the last few months. */
export function dayLocal(stamp: string | null | undefined): string {
  const t = parseUtc(stamp);
  if (t === null) return '—';
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short' }).format(new Date(t));
}

/** Money, always with its cents. $0.84, $6.20, $0.00 — never "$.84" or "$6.2". */
export function usd(n: number | null | undefined): string {
  return `$${(Number(n) || 0).toFixed(2)}`;
}
